import {WagerMode} from "../../generated/bay_session_shared_pb";

/**
 * Wager CATEGORY selection helpers (SMG-3446).
 *
 * The mobile picker is a CATEGORY selector — "Real Money" (MONEY) vs
 * "Points" (POINTS). The concrete points wallet token (TGPTS / FSPTS) is
 * resolved SERVER-side from venue_partner config; the client only chooses
 * the coarse category. PRACTICE is handled by the existing PlayModeModal
 * free-play flow and is deliberately NOT a member of this currency picker.
 *
 * Availability source: SessionPlayer.allowed_wager_modes (the wager
 * categories the station offers for this player) — bridged onto Context as
 * `allowedWagerModes` by Main.tsx's per-user stream dispatcher.
 */

/** The two categories the mobile currency picker can offer. */
export type SelectableWagerCategory = WagerMode.MONEY | WagerMode.POINTS;

/**
 * Concrete wallet token the AcceptBet currency string is stamped with for a
 * given category. MONEY → USD. POINTS → '' (empty): the client does NOT know
 * which points token (TGPTS / FSPTS) the venue uses, so it sends an empty
 * currency and lets the server resolve it from the sticky next_wager_currency
 * (set by SetWagerMode / SwitchWagerCurrency) + venue_partner config. AcceptBet
 * falls back to next_wager_currency when the request currency is empty
 * (design §4.3).
 */
export function currencyForCategory(category: SelectableWagerCategory): string {
  return category === WagerMode.MONEY ? 'USD' : '';
}

/**
 * The target_currency to hand SwitchWagerCurrency when re-minting an open
 * wager into `category`. MONEY → 'USD'. POINTS → the station's points token if
 * the client knows it, else '' so the server resolves it from venue_partner
 * config. The client generally does NOT know the concrete points token (it is
 * not on SessionPlayer as a category-level field), so '' is the normal POINTS
 * value and the server-side resolution path is exercised.
 */
export function switchTargetForCategory(
  category: SelectableWagerCategory,
  knownPointsToken?: string,
): string {
  if (category === WagerMode.MONEY) return 'USD';
  return knownPointsToken && knownPointsToken.length > 0 ? knownPointsToken : '';
}

/**
 * Real-money funds floor (SMG-3446). When BOTH Real Money and Points are
 * available, the player defaults to Real Money UNLESS their available USD
 * balance is below this threshold, in which case they default to Points and a
 * banner explains the switch.
 *
 * 500 minor units = $5.00 (USD is tracked in cents on the wire —
 * CurrencyAvailability.available is in minor units). The actual funds gate is
 * enforced server-side at accept / switch; this client-side floor only picks
 * the *default* category so a near-empty wallet doesn't land the player on a
 * Real Money wager they can't fund.
 */
export const REAL_MONEY_MIN_USD_MINOR = 500;

/**
 * The funds-aware default resolution (SMG-3446), returned only when BOTH
 * categories are available (the selector-shown case). `category` is the
 * category to default to; `autoMovedToPointsInsufficientUSD` is true ONLY when
 * we would have defaulted to Real Money but moved the player to Points because
 * their available USD balance is below REAL_MONEY_MIN_USD_MINOR — the signal
 * the UI uses to render the informational banner.
 */
export type DefaultCategoryResolution = {
  category: SelectableWagerCategory;
  autoMovedToPointsInsufficientUSD: boolean;
};

/**
 * Resolve the funds-aware DEFAULT category for the both-available case.
 *
 * - usdAvailableMinor >= REAL_MONEY_MIN_USD_MINOR ($5) → default MONEY, no banner.
 * - usdAvailableMinor <  REAL_MONEY_MIN_USD_MINOR      → default POINTS, banner.
 *
 * `usdAvailableMinor` is the player's available USD balance in minor units
 * (cents). A missing balance (undefined) is treated as 0 → below the floor →
 * default Points. This function is ONLY meaningful when both MONEY and POINTS
 * are selectable; callers gate on that before using it.
 */
export function resolveDefaultCategory(
  usdAvailableMinor: number | undefined,
): DefaultCategoryResolution {
  const usd = usdAvailableMinor ?? 0;
  if (usd >= REAL_MONEY_MIN_USD_MINOR) {
    return {category: WagerMode.MONEY, autoMovedToPointsInsufficientUSD: false};
  }
  return {category: WagerMode.POINTS, autoMovedToPointsInsufficientUSD: true};
}

/**
 * The selectable currency categories (MONEY / POINTS) the station offers,
 * derived from SessionPlayer.allowed_wager_modes. NONE and PRACTICE are
 * filtered out — NONE is the inactive state and PRACTICE is owned by the
 * free-play modal, not the currency picker.
 *
 * Order is stable: MONEY first, POINTS second, so the rendered picker order
 * is deterministic.
 */
export function selectableCategories(
  allowed: readonly WagerMode[] | undefined,
): SelectableWagerCategory[] {
  const set = new Set(allowed ?? []);
  const out: SelectableWagerCategory[] = [];
  if (set.has(WagerMode.MONEY)) out.push(WagerMode.MONEY);
  if (set.has(WagerMode.POINTS)) out.push(WagerMode.POINTS);
  return out;
}

/**
 * Single-availability rule (SMG-3446 AC): the picker is shown ONLY when ≥2
 * selectable categories are available. With exactly one (or zero) the picker
 * is hidden and the sole category is auto-used.
 */
export function shouldShowCurrencySelector(
  allowed: readonly WagerMode[] | undefined,
): boolean {
  return selectableCategories(allowed).length >= 2;
}

/**
 * Resolve the effective selected category given the station's offered set, the
 * player's current wager_mode, and any explicit in-session choice.
 *
 * - When exactly one category is available, that category is forced (the
 *   selector is hidden; the explicit choice is ignored).
 * - When ≥2 are available, an explicit choice wins if it is still offered;
 *   otherwise fall back to the player's current wager_mode if that is a
 *   selectable category; otherwise the first offered category.
 * - When none are available, return undefined (no currency wager possible).
 */
export function resolveSelectedCategory(
  allowed: readonly WagerMode[] | undefined,
  currentWagerMode: WagerMode | undefined,
  explicitChoice: SelectableWagerCategory | undefined,
): SelectableWagerCategory | undefined {
  const options = selectableCategories(allowed);
  if (options.length === 0) return undefined;
  if (options.length === 1) return options[0];

  if (explicitChoice !== undefined && options.includes(explicitChoice)) {
    return explicitChoice;
  }
  if (
    (currentWagerMode === WagerMode.MONEY || currentWagerMode === WagerMode.POINTS) &&
    options.includes(currentWagerMode)
  ) {
    return currentWagerMode;
  }
  return options[0];
}
